/**
 * Suivi des erreurs du navigateur (ADR 0013).
 *
 * Le serveur dit au démarrage si le suivi est allumé : l'image du front est la
 * même en recette et en production, seul le `.env` du serveur change. Éteint,
 * le SDK n'est même pas téléchargé — il vit dans un morceau à part, chargé à
 * la demande — et toutes les fonctions ci-dessous ne font rien.
 *
 * Les erreurs ne partent pas vers GlitchTip directement : elles passent par
 * le relais de l'API, sur la même origine, qui les nettoie une seconde fois.
 */
import {
  CHEMIN_RELAIS_SUIVI_ERREURS,
  COLLECTE_MINIMALE_SUIVI,
  nettoyerEvenement,
  nettoyerFilAriane,
  schemaConfigurationSuiviErreurs,
} from '@afs/shared'
import { requete } from './api/client.js'

type Sdk = typeof import('./suivi-erreurs-sdk.js')

type Personne = { id: string; role: string } | null

let sdk: Sdk | null = null

/**
 * La dernière personne désignée. La session peut être restaurée AVANT que le
 * SDK ait fini de se charger (lecture de la configuration, puis morceau de
 * 94 Ko) : on la garde ici et on l'applique juste après `init`, sinon toutes
 * les erreurs de la journée partiraient sans utilisateur ni rôle.
 */
let personne: Personne = null

function appliquerPersonne(cible: Sdk): void {
  cible.setUser(personne ? { id: personne.id, role: personne.role } : null)
  cible.setTag('role', personne?.role)
}

/** Allume le suivi si le serveur le demande. N'échoue jamais : au pire, rien ne part. */
export async function demarrerSuiviErreurs(): Promise<void> {
  try {
    const { dsn, environnement, version } = await requete('/suivi-erreurs/configuration', {
      schema: schemaConfigurationSuiviErreurs,
    })
    if (!dsn) return

    const charge = await import('./suivi-erreurs-sdk.js')
    charge.init({
      dsn,
      environment: environnement,
      release: version,
      tunnel: CHEMIN_RELAIS_SUIVI_ERREURS,
      // Aucune intégration implicite : seules celles-ci, et ni relecture de
      // session, ni mesure de performance, ni suivi des sessions.
      defaultIntegrations: false,
      integrations: [
        charge.globalHandlersIntegration(),
        charge.linkedErrorsIntegration(),
        charge.dedupeIntegration(),
        charge.httpContextIntegration(),
        // Ni clics ni saisies : le sélecteur d'un bouton peut porter un nom. La
        // console n'est pas écoutée : l'intégration qui la lit n'est pas chargée.
        charge.breadcrumbsIntegration({ dom: false, sentry: false }),
      ],
      dataCollection: COLLECTE_MINIMALE_SUIVI,
      sendClientReports: false,
      beforeSend: (evenement) => nettoyerEvenement(evenement),
      beforeBreadcrumb: (element) => nettoyerFilAriane(element),
    })
    sdk = charge
    appliquerPersonne(charge)
  } catch {
    // Serveur injoignable, mode dégradé, morceau introuvable : l'application
    // fonctionne sans suivi des erreurs, c'est tout.
  }
}

export function suiviErreursActif(): boolean {
  return sdk !== null
}

/** Désigne la personne connectée par son identifiant et son rôle — rien d'autre. */
export function identifierPourSuiviErreurs(utilisateur: Personne): void {
  personne = utilisateur ? { id: utilisateur.id, role: utilisateur.role } : null
  if (sdk) appliquerPersonne(sdk)
}

/** Signale une erreur rattrapée (frontière d'erreur, essai). */
export function signalerErreur(erreur: unknown): void {
  sdk?.captureException(erreur)
}

/** L'identifiant GlitchTip de la dernière erreur partie, joint à un signalement (décision 146). */
export function dernierEvenementSuivi(): string | null {
  return sdk?.lastEventId() ?? null
}
